import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import useAuthStore from './store/authStore';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/layout/DashboardLayout';
import DashboardPage from './pages/DashboardPage';
import BatchesPage from './pages/BatchesPage';
import BatchDetailPage from './pages/BatchDetailPage';
import DrawsPage from './pages/DrawsPage';
import MembersPage from './pages/MembersPage';
import MemberDetailPage from './pages/MemberDetailPage';
import StaffPage from './pages/StaffPage';
import SettingsPage from './pages/SettingsPage';
import ReportsPage from './pages/ReportsPage';
import DepositsPage from './pages/DepositsPage';
import WinnersPage from './pages/WinnersPage';
import NotificationsPage from './pages/NotificationsPage';
import NotFoundPage from './pages/NotFoundPage';
import ForbiddenPage from './pages/ForbiddenPage';
import RoleProtectedRoute from './components/common/RoleProtectedRoute';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function PrivateRoute({ children }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forbidden" element={<ForbiddenPage />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <DashboardLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            
            {/* Admin only routes */}
            <Route path="batches" element={
              <RoleProtectedRoute allowedRoles={['admin']}>
                <BatchesPage />
              </RoleProtectedRoute>
            } />
            <Route path="batches/:id" element={
              <RoleProtectedRoute allowedRoles={['admin']}>
                <BatchDetailPage />
              </RoleProtectedRoute>
            } />
            <Route path="draws" element={
              <RoleProtectedRoute allowedRoles={['admin']}>
                <DrawsPage />
              </RoleProtectedRoute>
            } />
            <Route path="staff" element={
              <RoleProtectedRoute allowedRoles={['admin']}>
                <StaffPage />
              </RoleProtectedRoute>
            } />
            <Route path="settings" element={
              <RoleProtectedRoute allowedRoles={['admin']}>
                <SettingsPage />
              </RoleProtectedRoute>
            } />
            <Route path="reports" element={
              <RoleProtectedRoute allowedRoles={['admin']}>
                <ReportsPage />
              </RoleProtectedRoute>
            } />
            
            {/* Admin and Staff routes */}
            <Route path="members" element={
              <RoleProtectedRoute allowedRoles={['admin', 'staff']}>
                <MembersPage />
              </RoleProtectedRoute>
            } />
            <Route path="members/:id" element={
              <RoleProtectedRoute allowedRoles={['admin', 'staff']}>
                <MemberDetailPage />
              </RoleProtectedRoute>
            } />
            <Route path="deposits" element={
              <RoleProtectedRoute allowedRoles={['admin', 'staff']}>
                <DepositsPage />
              </RoleProtectedRoute>
            } />
            <Route path="winners" element={
              <RoleProtectedRoute allowedRoles={['admin', 'staff']}>
                <WinnersPage />
              </RoleProtectedRoute>
            } />
            <Route path="notifications" element={
              <RoleProtectedRoute allowedRoles={['admin', 'staff']}>
                <NotificationsPage />
              </RoleProtectedRoute>
            } />
          </Route>
          
          {/* 404 catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </QueryClientProvider>
  );
}

export default App;
